/**
 * Vehicle Details Modal component for VehicleInformationStep
 */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { Box } from 'atoms';
import { getSignedUrl } from 'store/commonSlice';
import {
	SkipDocumentUploadRow,
	SkipDocumentCheckbox,
	SkipDocumentInfoIcon,
} from 'pagesv2/vehiclesv2/create-vehiclev2/CreateVehiclePageV2.styles';
import {
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
} from 'pagesv2/vehiclesv2/create-vehiclev2/CreateVehiclePageV2.constants';
import { StyledModal } from './VehicleInformationStep.styled';
import {
	getFileKeyFieldName,
	validateFileSize,
	isPersistedVehicle,
} from './VehicleInformationStep.helpers';
import { UPLOADING_TEXT, BROWSE_FILE_TEXT } from './VehicleInformationStep.constants';

// eslint-disable-next-line sonarjs/cognitive-complexity
function VehicleDetailsModal({
	visible,
	selectedVehicle,
	editingVehicle,
	setEditingVehicle,
	disabled,
	savingVehicle,
	vehicleTypeOptions,
	loadingVehicleTypes,
	onClose,
	onSave,
	onRemove,
	handleDocumentPreview,
	FormField,
	StyledSelect,
	StyledInput,
	StyledDatePicker,
	BrowseButton,
	RemoveButton,
	SaveButton,
	FilePreview,
	FileIcon,
	FileName,
	// FileSize,
	RemoveFileButton,
	ConsentLetterBrowseButton,
	DownloadOutlined,
	EyeOutlined,
	Option,
	message: messageInstance,
}) {
	const dispatch = useDispatch();

	const isNewVehicle = !isPersistedVehicle(selectedVehicle);
	const effectiveSkip = isNewVehicle && Boolean(editingVehicle?.skip_document_upload);

	// File input refs for modal
	const rcDocFileInputRef = useRef(null);
	const insuranceFileInputRef = useRef(null);
	const pucDocFileInputRef = useRef(null);
	const consentLetterFileInputRef = useRef(null);

	// Uploading state for modal
	const [uploading, setUploading] = useState({
		rc_doc: false,
		insurance: false,
		puc_doc: false,
		consent_letter: false,
	});

	const [vehicleNumberError, setVehicleNumberError] = useState('');
	const [pucValidTillError, setPucValidTillError] = useState('');

	// Ensure file inputs are accessible when modal opens
	useEffect(() => {
		if (visible) {
			// Small delay to ensure modal is fully rendered
			const timer = setTimeout(() => {
				// Verify refs are set
				if (!rcDocFileInputRef.current) {
					console.warn('RC doc file input ref not initialized');
				}
			}, 100);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [visible]);

	// Early return only if modal is not visible
	if (!visible) {
		return null;
	}

	if (!selectedVehicle || !editingVehicle) {
		return null;
	}

	// Handle file upload in modal
	const handleModalFileChange = async (event, fieldName) => {
		if (disabled || savingVehicle || effectiveSkip) return;

		const file = event.target.files?.[0];
		if (!file) return;

		// Validate file size
		if (!validateFileSize(file, messageInstance)) {
			return;
		}

		// Set uploading state
		setUploading((prev) => ({ ...prev, [fieldName]: true }));

		try {
			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			// Step 3: Update editingVehicle with file key
			const fileKeyField = getFileKeyFieldName(fieldName);
			setEditingVehicle({
				...editingVehicle,
				[fileKeyField]: fileData.filename,
				[`${fieldName}_file`]: file,
			});

			messageInstance.success('File uploaded successfully');
		} catch (error) {
			console.error('File upload error:', error);
			messageInstance.error('Failed to upload file');
		} finally {
			setUploading((prev) => ({ ...prev, [fieldName]: false }));
			// Reset file input
			if (event.target) {
				event.target.value = '';
			}
		}
	};

	// Handle file upload button click in modal
	const handleModalFileUploadClick = (fieldName) => {
		if (disabled || savingVehicle || effectiveSkip) return;

		const refMap = {
			rc_doc: rcDocFileInputRef,
			insurance: insuranceFileInputRef,
			puc_doc: pucDocFileInputRef,
			consent_letter: consentLetterFileInputRef,
		};

		const fileInputRef = refMap[fieldName];
		if (fileInputRef?.current) {
			fileInputRef.current.click();
		}
	};

	return (
		<StyledModal
			title="Vehicle Information"
			open={visible}
			onCancel={onClose}
			width={900}
			footer={[
				<RemoveButton key="remove" onClick={onRemove} disabled={disabled || savingVehicle}>
					Remove Vehicle
				</RemoveButton>,
				<SaveButton
					key="save"
					onClick={onSave}
					disabled={disabled || savingVehicle}
					loading={savingVehicle}
				>
					Save Changes
				</SaveButton>,
			]}
		>
			{/* Hidden file inputs for modal */}
			<input
				key="rc-doc-input"
				type="file"
				ref={rcDocFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled || savingVehicle}
				onChange={(e) => handleModalFileChange(e, 'rc_doc')}
			/>
			<input
				key="insurance-input"
				type="file"
				ref={insuranceFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled || savingVehicle}
				onChange={(e) => handleModalFileChange(e, 'insurance')}
			/>
			<input
				key="puc-doc-input"
				type="file"
				ref={pucDocFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled || savingVehicle}
				onChange={(e) => handleModalFileChange(e, 'puc_doc')}
			/>
			<input
				key="consent-letter-input"
				type="file"
				ref={consentLetterFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled || savingVehicle}
				onChange={(e) => handleModalFileChange(e, 'consent_letter')}
			/>
			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormField label="Vehicle Type" required>
						<StyledSelect
							showSearch
							placeholder={
								loadingVehicleTypes
									? 'Loading vehicle types...'
									: 'Search and select'
							}
							value={editingVehicle.vehicle_type_id}
							onChange={(value) =>
								setEditingVehicle({
									...editingVehicle,
									vehicle_type_id: value,
									vehicleType:
										vehicleTypeOptions.find((opt) => opt.id === value)?.label ||
										'',
								})
							}
							disabled={disabled || savingVehicle || loadingVehicleTypes}
							loading={loadingVehicleTypes}
							filterOption={(input, option) =>
								(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
							}
							notFoundContent={
								loadingVehicleTypes ? 'Loading...' : 'No vehicle types found'
							}
						>
							{vehicleTypeOptions.map((option) => (
								<Option key={option.id} value={option.id}>
									{option.label}
								</Option>
							))}
						</StyledSelect>
					</FormField>
				</Col>
				<Col span={12}>
					<FormField label="Vehicle Number" required>
						<StyledInput
							placeholder="Eg : DL01AB1234, 22BH1234AB"
							value={editingVehicle.vehicle_number || editingVehicle.vehicleNumber}
							onChange={(e) => {
								setEditingVehicle({
									...editingVehicle,
									vehicle_number: e.target.value,
									vehicleNumber: e.target.value,
								});
								if (vehicleNumberError) {
									setVehicleNumberError('');
								}
							}}
							onBlur={(e) => {
								const { value } = e.target;
								if (value && !VEHICLE_NUMBER_PATTERN.test(value)) {
									setVehicleNumberError(VEHICLE_NUMBER_MESSAGE);
								} else {
									setVehicleNumberError('');
								}
							}}
							status={vehicleNumberError ? 'error' : ''}
							disabled={disabled || savingVehicle}
						/>
						{vehicleNumberError && (
							<div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
								{vehicleNumberError}
							</div>
						)}
					</FormField>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col span={6}>
					<FormField label="Vehicle RC Doc Upload" required={!disabled && !effectiveSkip}>
						{editingVehicle.rc_document_key ? (
							<FilePreview>
								<FileIcon>📄</FileIcon>
								<FileName>RC Document</FileName>
								{/* <FileSize>Uploaded</FileSize> */}
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleDocumentPreview(
												editingVehicle.rc_document_key,
												'RC Document'
											)
										}
									/>
									<RemoveFileButton
										onClick={() =>
											setEditingVehicle({
												...editingVehicle,
												rc_document_key: '',
												rc_doc_file: null,
											})
										}
									>
										×
									</RemoveFileButton>
								</Box>
							</FilePreview>
						) : (
							<BrowseButton
								icon={<DownloadOutlined />}
								disabled={disabled || savingVehicle || effectiveSkip}
								loading={uploading.rc_doc}
								onClick={() => handleModalFileUploadClick('rc_doc')}
								type="button"
							>
								{uploading.rc_doc ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
							</BrowseButton>
						)}
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Valid Till" required={!disabled && !effectiveSkip}>
						<StyledDatePicker
							placeholder="Eg : 12-2-2025"
							format="DD-MM-YYYY"
							value={editingVehicle.rc_valid_till}
							onChange={(date) =>
								setEditingVehicle({
									...editingVehicle,
									rc_valid_till: date,
								})
							}
							disabled={disabled || savingVehicle || effectiveSkip}
						/>
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Insurance Doc Upload" required={!disabled && !effectiveSkip}>
						{editingVehicle.insurance_document_key ? (
							<FilePreview>
								<FileIcon>📄</FileIcon>
								<FileName>Insurance Document</FileName>
								{/* <FileSize>Uploaded</FileSize> */}
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleDocumentPreview(
												editingVehicle.insurance_document_key,
												'Insurance Document'
											)
										}
									/>
									<RemoveFileButton
										onClick={() =>
											setEditingVehicle({
												...editingVehicle,
												insurance_document_key: '',
												insurance_file: null,
											})
										}
									>
										×
									</RemoveFileButton>
								</Box>
							</FilePreview>
						) : (
							<BrowseButton
								icon={<DownloadOutlined />}
								disabled={disabled || savingVehicle || effectiveSkip}
								loading={uploading.insurance}
								onClick={() => handleModalFileUploadClick('insurance')}
								type="button"
							>
								{uploading.insurance ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
							</BrowseButton>
						)}
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Valid Till" required={!disabled && !effectiveSkip}>
						<StyledDatePicker
							placeholder="Eg : 12-2-2025"
							format="DD-MM-YYYY"
							value={editingVehicle.insurance_valid_till}
							onChange={(date) =>
								setEditingVehicle({
									...editingVehicle,
									insurance_valid_till: date,
								})
							}
							disabled={disabled || savingVehicle || effectiveSkip}
						/>
					</FormField>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col span={6}>
					<FormField label="PUC Doc Upload">
						{editingVehicle.puc_document_key ? (
							<FilePreview>
								<FileIcon>📄</FileIcon>
								<FileName>PUC Document</FileName>
								{/* <FileSize>Uploaded</FileSize> */}
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleDocumentPreview(
												editingVehicle.puc_document_key,
												'PUC Document'
											)
										}
									/>
									<RemoveFileButton
										onClick={() =>
											setEditingVehicle({
												...editingVehicle,
												puc_document_key: '',
												puc_doc_file: null,
											})
										}
									>
										×
									</RemoveFileButton>
								</Box>
							</FilePreview>
						) : (
							<BrowseButton
								icon={<DownloadOutlined />}
								disabled={disabled || savingVehicle || effectiveSkip}
								loading={uploading.puc_doc}
								onClick={() => handleModalFileUploadClick('puc_doc')}
								type="button"
							>
								{uploading.puc_doc ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
							</BrowseButton>
						)}
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Valid Till" required={!!editingVehicle.puc_document_key}>
						<StyledDatePicker
							placeholder="Eg : 12-2-2025"
							format="DD-MM-YYYY"
							value={editingVehicle.puc_valid_till}
							onChange={(date) => {
								setEditingVehicle({
									...editingVehicle,
									puc_valid_till: date,
								});
								if (pucValidTillError && date) {
									setPucValidTillError('');
								}
							}}
							onBlur={() => {
								if (
									editingVehicle.puc_document_key &&
									!editingVehicle.puc_valid_till
								) {
									setPucValidTillError(
										'PUC Valid Till is required when PUC document is uploaded'
									);
								} else {
									setPucValidTillError('');
								}
							}}
							status={pucValidTillError ? 'error' : ''}
							disabled={disabled || savingVehicle || effectiveSkip}
						/>
						{pucValidTillError && (
							<div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
								{pucValidTillError}
							</div>
						)}
					</FormField>
				</Col>
				<Col span={12}>
					<FormField label="Owner Ship Type" required={!disabled && !effectiveSkip}>
						<StyledSelect
							placeholder="Select"
							value={editingVehicle.ownership_type}
							onChange={(value) => {
								const updatedVehicle = {
									...editingVehicle,
									ownership_type: value,
								};
								// Clear consent letter if changing to Own
								if (value === 'Own') {
									updatedVehicle.consent_letter_key = null;
									updatedVehicle.consent_letter_file = null;
								}
								setEditingVehicle(updatedVehicle);
							}}
							disabled={disabled || savingVehicle || effectiveSkip}
						>
							<Option value="Own">Own</Option>
							<Option value="Aggregator">Aggregator</Option>
						</StyledSelect>
					</FormField>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col span={24}>
					<FormField
						label="Upload Consent Letter"
						required={
							!disabled &&
							!effectiveSkip &&
							editingVehicle.ownership_type === 'Aggregator'
						}
					>
						<ConsentLetterBrowseButton
							icon={<DownloadOutlined />}
							disabled={
								disabled ||
								savingVehicle ||
								effectiveSkip ||
								!editingVehicle.ownership_type
							}
							loading={uploading.consent_letter}
							onClick={() => handleModalFileUploadClick('consent_letter')}
							type="button"
						>
							{uploading.consent_letter ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
						</ConsentLetterBrowseButton>
						{editingVehicle.consent_letter_key && (
							<FilePreview style={{ marginTop: '12px' }}>
								<FileIcon>📄</FileIcon>
								<FileName>Consent Letter</FileName>
								{/* <FileSize>Uploaded</FileSize> */}
								<Box display="flex" alignItems="center" gap={8}>
									<EyeOutlined
										style={{
											cursor: 'pointer',
											color: '#1890ff',
											fontSize: '16px',
										}}
										onClick={() =>
											handleDocumentPreview(
												editingVehicle.consent_letter_key,
												'Consent Letter'
											)
										}
									/>
									<RemoveFileButton
										onClick={() =>
											setEditingVehicle({
												...editingVehicle,
												consent_letter_key: '',
												consent_letter_file: null,
											})
										}
									>
										×
									</RemoveFileButton>
								</Box>
							</FilePreview>
						)}
					</FormField>
				</Col>
			</Row>

			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormField label="GPS Installed" required={!disabled && !effectiveSkip}>
						<StyledSelect
							placeholder="Select"
							value={editingVehicle.gps_installed}
							onChange={(value) =>
								setEditingVehicle({
									...editingVehicle,
									gps_installed: value,
									gps_provider_name:
										value === true ? editingVehicle.gps_provider_name : '',
								})
							}
							disabled={disabled || savingVehicle || effectiveSkip}
						>
							<Option value>Yes</Option>
							<Option value={false}>No</Option>
						</StyledSelect>
					</FormField>
				</Col>
				{editingVehicle.gps_installed === true && (
					<Col span={12}>
						<FormField label="GPS Provider Name" required={!disabled && !effectiveSkip}>
							<StyledInput
								placeholder="Enter Provider Name"
								value={editingVehicle.gps_provider_name || ''}
								onChange={(e) =>
									setEditingVehicle({
										...editingVehicle,
										gps_provider_name: e.target.value,
									})
								}
								disabled={disabled || savingVehicle || effectiveSkip}
							/>
						</FormField>
					</Col>
				)}
			</Row>

			{isNewVehicle && (
				<Row gutter={[24, 0]}>
					<Col span={24}>
						<FormField label="Skip Document Upload">
							<SkipDocumentUploadRow style={{ opacity: savingVehicle ? 0.8 : 1 }}>
								<SkipDocumentCheckbox
									checked={editingVehicle.skip_document_upload}
									disabled={disabled || savingVehicle}
									onChange={(e) => {
										if (e.target.checked) {
											setEditingVehicle({
												...editingVehicle,
												skip_document_upload: true,
												rc_document_key: '',
												rc_doc_file: null,
												rc_valid_till: null,
												insurance_document_key: '',
												insurance_file: null,
												insurance_valid_till: null,
												puc_document_key: '',
												puc_doc_file: null,
												puc_valid_till: null,
												ownership_type: '',
												consent_letter_key: '',
												consent_letter_file: null,
											});
										} else {
											setEditingVehicle({
												...editingVehicle,
												skip_document_upload: false,
											});
										}
									}}
								>
									SKIP DOCUMENT UPLOAD
								</SkipDocumentCheckbox>
								<Tooltip
									title="A vehicle can be onboarded by skipping the documents for now. But a trip cannot be closed if the vehicle documents are not present."
									placement="right"
								>
									<SkipDocumentInfoIcon>i</SkipDocumentInfoIcon>
								</Tooltip>
							</SkipDocumentUploadRow>
						</FormField>
					</Col>
				</Row>
			)}
		</StyledModal>
	);
}

VehicleDetailsModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	selectedVehicle: PropTypes.object,
	editingVehicle: PropTypes.object,
	setEditingVehicle: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	savingVehicle: PropTypes.bool,
	vehicleTypeOptions: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			label: PropTypes.string,
			value: PropTypes.number,
		})
	).isRequired,
	loadingVehicleTypes: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	handleDocumentPreview: PropTypes.func.isRequired,
	FormField: PropTypes.elementType.isRequired,
	StyledSelect: PropTypes.elementType.isRequired,
	StyledInput: PropTypes.elementType.isRequired,
	StyledDatePicker: PropTypes.elementType.isRequired,
	BrowseButton: PropTypes.elementType.isRequired,
	RemoveButton: PropTypes.elementType.isRequired,
	SaveButton: PropTypes.elementType.isRequired,
	FilePreview: PropTypes.elementType.isRequired,
	FileIcon: PropTypes.elementType.isRequired,
	FileName: PropTypes.elementType.isRequired,
	// FileSize: PropTypes.elementType.isRequired,
	RemoveFileButton: PropTypes.elementType.isRequired,
	ConsentLetterBrowseButton: PropTypes.elementType.isRequired,
	DownloadOutlined: PropTypes.elementType.isRequired,
	EyeOutlined: PropTypes.elementType.isRequired,
	Option: PropTypes.elementType.isRequired,
	message: PropTypes.object.isRequired,
};

export { VehicleDetailsModal };
